/**
 * Application Designer — FLOW layout geometry.
 *
 * Flow is the mode for content-sized native components. A Problem List is as
 * tall as the patient's problem list, so its height is not the designer's to
 * choose: an item's position is a column plus an ordinal within that column,
 * and the browser lays the column out. Nothing here stores a height, a pixel,
 * or a row.
 *
 * This is the geometry Patient Summary's Design View already used, lifted out
 * of `patientSummaryLayout` with the column count made a parameter and the
 * item catalog passed in. `lib/patientSummaryLayout.js` now delegates to it, so
 * the behaviour the existing Design View tests pin is defined in one place and
 * any other flow page inherits it.
 *
 * The GRID counterpart is `gridLayout.js`, which binds `lib/designerGrid.js`
 * for pages whose elements really do have designer-chosen rectangles. The two
 * modes deliberately do NOT share geometry — only selection, properties,
 * cosmetics and persistence.
 *
 * Every function takes and returns a plain array of items and never mutates its
 * argument, so a caller can treat the result as the next draft.
 */

/**
 * A stored column, or the element's declared default when the stored value is
 * not a usable one. Numeric strings are accepted because JSON round trips them,
 * but `null`, `""` and booleans are NOT: they coerce to 0 in JavaScript, and a
 * stored `{"column": null}` must leave the element at its declared column rather
 * than silently move it to the first one.
 */
function numericOr(value, fallback) {
  if (value === null || value === undefined || value === "" || typeof value === "boolean") {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
}

export function clampFlowColumn(value, fallback, columnCount) {
  const column = numericOr(value, NaN);
  if (Number.isInteger(column) && column >= 0 && column < columnCount) return column;
  return fallback;
}

export function toFlowOrder(value, fallback) {
  const order = numericOr(value, NaN);
  return Number.isFinite(order) ? order : fallback;
}

/**
 * Renumbers each column to 0..n-1 with visible items first, so an order is
 * always a small dense integer and two layouts that look identical serialize
 * identically. Hidden items keep a stable slot rather than collapsing to 0.
 */
export function compactFlowOrders(items, columnCount) {
  for (let column = 0; column < columnCount; column += 1) {
    const inColumn = items
      .filter((item) => item.column === column)
      .sort((left, right) => {
        if (left.visible !== right.visible) return left.visible ? -1 : 1;
        if (left.order !== right.order) return left.order - right.order;
        return left.id.localeCompare(right.id);
      });
    inColumn.forEach((item, index) => {
      item.order = index;
    });
  }
  return items;
}

export function visibleItemsInColumn(items, column) {
  return (items || [])
    .filter((item) => item.visible && item.column === column)
    .sort((left, right) => left.order - right.order || left.id.localeCompare(right.id));
}

export function hiddenItems(items, titleOf = (id) => id) {
  return (items || [])
    .filter((item) => !item.visible)
    .sort((left, right) => String(titleOf(left.id)).localeCompare(String(titleOf(right.id))));
}

/**
 * Moves `id` into `column`, immediately before `beforeId` or at the end.
 * Placing an item always reveals it — dragging something out of the hidden
 * tray and dropping it into a column is one gesture, not two.
 */
export function placeFlowItem(items, id, column, beforeId = null, { columnCount, known } = {}) {
  if (known && !known(id)) return items;
  const next = (items || []).map((item) => ({ ...item }));
  const target = next.find((item) => item.id === id);
  if (!target) return items;
  const destination = clampFlowColumn(column, target.column, columnCount);
  target.visible = true;
  target.column = destination;
  const siblings = visibleItemsInColumn(
    next.filter((item) => item.id !== id),
    destination,
  );
  let insertAt = siblings.length;
  if (beforeId) {
    const index = siblings.findIndex((item) => item.id === beforeId);
    if (index >= 0) insertAt = index;
  }
  siblings.splice(insertAt, 0, target);
  siblings.forEach((item, index) => {
    item.order = index;
  });
  return compactFlowOrders(next, columnCount);
}

export function hideFlowItem(items, id) {
  return (items || []).map((item) => (item.id === id ? { ...item, visible: false } : item));
}

export function showFlowItem(items, id) {
  return (items || []).map((item) => (item.id === id ? { ...item, visible: true } : item));
}

/**
 * Keyboard/button movement in the four directions a flow column supports.
 * Moving down past the last sibling, or left out of column 0, is a no-op that
 * returns the original array so a caller can treat identity as "nothing moved".
 */
export function moveFlowItem(items, id, direction, { columnCount, known } = {}) {
  const current = (items || []).find((item) => item.id === id);
  if (!current || !current.visible) return items;
  const options = { columnCount, known };
  const siblings = visibleItemsInColumn(items, current.column);
  const index = siblings.findIndex((item) => item.id === id);
  if (direction === "up") {
    if (index <= 0) return items;
    return placeFlowItem(items, id, current.column, siblings[index - 1].id, options);
  }
  if (direction === "down") {
    if (index < 0 || index >= siblings.length - 1) return items;
    const following = siblings[index + 2];
    return placeFlowItem(items, id, current.column, following ? following.id : null, options);
  }
  if (direction === "left" && current.column > 0) {
    return placeFlowItem(items, id, current.column - 1, null, options);
  }
  if (direction === "right" && current.column < columnCount - 1) {
    return placeFlowItem(items, id, current.column + 1, null, options);
  }
  return items;
}
